import { Catalog } from './../../models/promotion-catalog.model';
import { HttpClientService } from './../httpClient/httpclient.service';
import { ListMetadata } from './../../models/list-metadata.model';
import { Observable } from 'rxjs/Rx';
import { Injectable } from '@angular/core';
import { publishReplay, refCount } from 'rxjs/operators';
import { MessageService } from 'primeng/primeng';

@Injectable()
export class PromotionCatalogService {
  baseUrl = '/koncentro-promo';
  //baseUrlGiftPreferred = 'https://api-feature-01-ext.unicomm.koncentro.cloud/k7o/gui-shim/loy/gift/sys/preferred';
  baseUrlGiftPreferred = '/k7o/gui-shim/loy/gift/sys/preferred';
  cachedCatalogFields: Observable<any>;
  cachedSndFields: Observable<any>;
  cachedStoreFields: Observable<any>;
  userId: string;

  constructor(private http: HttpClientService,
    private messageService: MessageService

  ) { }

  /**
    * Tramite metodo GET, restituisce la lista dei cataloghi premi.
    * Prevede obbligatoriamente un offset ed un limite, permettendo la paginazione della lista.
  */
  list(
    offset = null,
    limit = null,
    search?: Array<{ key: string, value: string }>,
    globalSearch?: string,
    sort?: string,
    sort_dir?: number
  ): Observable<{ data: Array<Catalog>, metadata: ListMetadata }> {
    const params = HttpClientService.buildListParams(offset, limit, search, globalSearch, sort, sort_dir);
    return this.http.getPaged(this.baseUrl + '/catalog/getCatalogList' +
      (params.toString() ? '?' + params.toString().replace(/\+/gi, '%2B').replace(/'/gi, "''") : '' ));
  }

  /**
   * DTO Form Catalogo
   */
  getCatalogFields() {
    if (!this.cachedCatalogFields) {
      this.cachedCatalogFields = this.http.get(`${this.baseUrl}/schema/CatalogDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedCatalogFields;
  }

  /**
   * DTO Form Dettagli Validità Divisione Rete Vendita
   */
  getSndFields() {
    if (!this.cachedSndFields) {
      this.cachedSndFields = this.http.get(`${this.baseUrl}/schema/CatalogDivSalNetDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedSndFields;
  }

  /**
   * DTO Form Dettagli Negozio
   */
  getStoreFields() {
    if (!this.cachedStoreFields) {
      this.cachedStoreFields = this.http.get(`${this.baseUrl}/schema/CatalogStoreExclDto`).pipe(
        publishReplay(1), // this tells Rx to cache the latest emitted
        refCount() // and this tells Rx to keep the Observable alive as long as there are any Subscribers
      )
    }
    return this.cachedStoreFields;
  }

  /**
   * Popola Form Catalogo
   */
  getCatalogData(catalogId: number) {
    return this.http.get(`${this.baseUrl}/catalog/getCatalogList/${catalogId}`)
  }

  /**
   * Popola Form Dettagli Validità Divisione Rete Vendita
   */
  getSndData(catalogId: number) {
    return this.http.get(`${this.baseUrl}/catalog/getCatalogList/detailValidation/${catalogId}`)
  }

  /**
   * Popola Form Dettagli Negozio
   */
  getStoreData(sndId: number) {
    return this.http.get(`${this.baseUrl}/catalog/getCatalogList/storeValidation/${sndId}`)
  }

  /**
   * Popola Dropdown Accumulatori
   */
  getAccumulators(channelId: number) {
    if (channelId) {
      return this.http.get(`/koncentro-loyalty/acm/getAcmListMulti?channel_id=${channelId}`);
    } else {
      return this.http.get(`/koncentro-loyalty/acm/getAcmListMulti`);
    }
  }

  /**
   * Salva un Nuovo Catalogo
   */
  createCatalog(data: any) {
    return this.http.post(`${this.baseUrl}/catalog/createCatalog`, data);
  }

  /**
   * Modifica un Catalogo esistente
   */
  updateCatalog(id: number, data: any) {
    return this.http.put(`${this.baseUrl}/catalog/updateCatalog/${id}`, data);
  }

  /**
   * Elimina Catalogo
   */
  deleteCatalog(id: number) {
    return this.http.delete(`${this.baseUrl}/catalog/deleteCatalog/${id}`);
  }

  /**
   * Aggiunge una nuova Divisione Rete Vendita al Catalogo
   */
  addSnd(catalogId: number, data: any) {
    return this.http.post(`${this.baseUrl}/catalog/${catalogId}/addDetailValidationToCatalog`, data);
  }

  /**
   * Modifica una Divisione Rete Vendita del Catalogo
   */
  updateSnd(catalogId: number, sndId: number, data: any) {
    return this.http.put(`${this.baseUrl}/catalog/${catalogId}/updateDetailValidationToCatalog/${sndId}`, data);
  }

  /**
   * Elimina una Divisione Rete Vendita del Catalogo
   */
  deleteSnd(sndId: number) {
    return this.http.delete(`${this.baseUrl}/catalog/deleteDetailValidationFromCatalog/${sndId}`);
  }

  /**
   * Esclude un Negozio/Gruppo ad una Divisione Rete Vendita del Catalogo
   */
  excludeStore(data: any) {
    return this.http.post(`${this.baseUrl}/catalog/exclStoreFromCatalog`, data);
  }

  /**
   * Elimina un Negozio/Gruppo da una Divisione Rete Vendita del Catalogo
   */
  deleteStore(storeId: number) {
    return this.http.delete(`${this.baseUrl}/catalog/deleteStoreFromCatalog/${storeId}`);
  }

  /**
   * Restituisce la lista dei Negozi della Sales Network Division
   */
  getSndStoresList(sndId: number) {
    return this.http.get(`/koncentro-common/store?division_sales_network=${sndId}`)
  }

  /**
   * Restituisce lista Gruppi di Negozi
   */
  getStoreGroups(sndId: number) {
    return this.http.get(`koncentro-common/store-group/division_sales_net/${sndId}`);
  }

  /**
   * Restituisce una lista di Division contenenti nel nome la stringa ricercata
   */
  getDivisions(divisionName: string) {
    return this.http.get(`koncentro-common/store/store_divisions/user/${this.userId}?flg_state=0&name=${divisionName}`)

  }

  /**
   * Restituisce una lista di Reti Vendita, relativi alla Division con l'id selezionato,
   * contenenti nel nome la stringa ricercata
   */
  getSalesNetwork(divisionId: number, snName: string) {
    return this.http.get(`koncentro-common/store/store_divisions/user/${this.userId}?div_sal_net_id=${divisionId}&sales_network_name=${snName}`)
  }

  /**
   * Restituisce gli header dei premi importati
   * @returns 
   */
  getCatalogImportGift() {
    return this.http.get(`/koncentro-promo/catalogImportGift/header`)
  }

  /**
   * Dato un gruppo di Articoli li importa nel catalogo scelto
   * @param catalogId l'id del catalogo
   * @param headId l'id dell'header del gruppo di articoli
   * @param update flg per l'aggiornamento dei valori
   * @param importData il body 
   * @returns 
   */
  importArtcle(catalogId, headId, update){
    return this.http.get(`/koncentro-promo/catalog/${catalogId}/updateFromImport/${headId}?update=${update}`);
  }

  getActiveCatalog(acmId) {
    return this.http.get(`${this.baseUrl}/catalog/getActiveCatalog?acm_id=${acmId}`)
  }

  getCatalogVisible(divisionId, acmId) {
    return this.http.get(`${this.baseUrl}/catalog-gift/getCatalogVisible?division_sales_network_id=${divisionId}&reservation=true&acm_id=${acmId}`)
  }

  /* chiamata gestione premi preferiti,  1 premio per catalogo */ 
  getCatalogPreferredGift(customerId: string,catalogId: number) {
    return this.http.get(`${this.baseUrlGiftPreferred}?customerId=${customerId}&catalogId=${catalogId}`)
  }

  editPreferredGift(data: any) {
    return this.http.put(`${this.baseUrlGiftPreferred}`, data);
 
  }

  deletePreferredGift(customerId: number, catalogId: number) {
    return this.http.delete(`${this.baseUrlGiftPreferred}?customerId=${customerId}&catalogId=${catalogId}`)
  }


}



// WEBPACK FOOTER //
// ./src/app/services/promotion/promotion-catalog.service.ts